"use client";

import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useState,
} from "react";
import { BatchResponseBody } from "@microsoft/microsoft-graph-client";
import {
  Fido2AuthenticationMethod,
  MicrosoftAuthenticatorAuthenticationMethod,
  PhoneAuthenticationMethod,
  TemporaryAccessPassAuthenticationMethod,
} from "@microsoft/microsoft-graph-types";

import { GraphTenant } from "@/types/microsoft";
import {
  AuthenticationMethods,
  AuthenticationMethodUpsertTypes,
  AuthenticationMethodMetadata,
  AuthenticationRequest,
  MultifactorContextType,
  SetAuthenticationMethodResponse,
} from "@/types/multifactor";

import { getErrorMessage } from "@/lib/data";
import { httpRequest } from "@/lib/http";

import { useAuth } from "@/context/util/auth";
import { useUi } from "@/context/util/ui";

const MultifactorContext = createContext<MultifactorContextType>(null!);

export function MultifactorProvider({ children }: { children: ReactNode }) {
  const { setLoading, setSnackbar } = useUi();
  const { getAccessToken } = useAuth();

  const [metadata, setMetadata] = useState<AuthenticationMethodMetadata | null>(
    null
  );

  const [data, setData] = useState<AuthenticationMethodUpsertTypes | null>(
    null
  );

  const getAuthenticationMethods = useCallback(
    async (upn: string, tenant: GraphTenant = "employee") => {
      try {
        const token = await getAccessToken();

        if (!token) throw new Error("No access token available");

        const requests = getAuthenticationMethodRequests(upn);

        const url = `/account-utilities/api/microsoft/graph/batch?tenant=${tenant}`;

        const body = JSON.stringify(requests);
        const headers = { Authorization: `Bearer ${token}` };
        const method = "POST";

        const requestInit: RequestInit = { body, headers, method };

        const authenticationMethods = (
          await httpRequest<BatchResponseBody>(url, requestInit)
        ).data;

        if (!authenticationMethods) throw new Error("Empty http response");

        const authentication: AuthenticationMethods = {
          authenticator: [],
          fido2: [],
          phone: [],
        };

        for (const a of authenticationMethods.responses) {
          switch (a.id) {
            case `${upn}-1`:
              authentication.fido2 = a.body.value
                ? (a.body.value as Fido2AuthenticationMethod[])
                : [];
              break;
            case `${upn}-2`:
              authentication.authenticator = a.body.value
                ? (a.body.value as MicrosoftAuthenticatorAuthenticationMethod[])
                : [];
              break;
            case `${upn}-3`:
              authentication.phone = a.body.value
                ? (a.body.value as PhoneAuthenticationMethod[])
                : [];
              break;
            case `${upn}-4`:
              authentication.tap = a.body.value
                ? (a.body.value[0] as TemporaryAccessPassAuthenticationMethod)
                : undefined;
              break;
          }
        }

        return authentication;
      } catch (error) {
        const message =
          "Failed to get user authentication methods. Please try again later.";
        console.error(message, error);

        setSnackbar((prev) => ({
          ...prev,
          message,
          open: true,
          severity: "error",
        }));

        return { fido2: [], authenticator: [], phone: [], tap: undefined };
      }
    },
    [getAccessToken, setLoading, setSnackbar]
  );

  async function addAuthenticationMethod(request: AuthenticationRequest) {
    const method = "POST";
    return await setAuthenticationMethod({ ...request, method });
  }

  async function deleteAuthenticationMethod(request: AuthenticationRequest) {
    const method = "DELETE";
    return await setAuthenticationMethod({ ...request, method });
  }

  function getAuthenticationMethodRequests(upn: string) {
    return {
      requests: [
        {
          id: `${upn}-1`,
          method: "GET",
          url: `users/${upn}/authentication/fido2Methods`,
        },
        {
          id: `${upn}-2`,
          method: "GET",
          url: `users/${upn}/authentication/microsoftAuthenticatorMethods`,
        },
        {
          id: `${upn}-3`,
          method: "GET",
          url: `users/${upn}/authentication/phoneMethods`,
        },
        {
          id: `${upn}-4`,
          method: "GET",
          url: `users/${upn}/authentication/temporaryAccessPassMethods`,
        },
      ],
    };
  }

  function getSetAuthenticationMethodUrl(request: AuthenticationRequest) {
    const { email, id, entity, method, tenant = "employee", upn } = request;

    const url = `/account-utilities/api/microsoft/graph/users/authentication?upn=${upn}&tenant=${tenant}&entity=${entity}`;

    if (id) return `${url}&id=${id}`;
    if (entity === "tap" && method === "POST" && email)
      return `${url}&email=${email.address}`;
    return url;
  }

  async function setAuthenticationMethod(request: AuthenticationRequest) {
    setLoading(true);

    try {
      const token = await getAccessToken();

      if (!token) throw new Error("No access token available");

      const url = getSetAuthenticationMethodUrl(request);

      const body = data ? JSON.stringify(data) : null;
      const headers = { Authorization: `Bearer ${token}` };
      const method = request.method;

      const requestInit: RequestInit = { body, headers, method };

      const setAuthenticationMethodData = (
        await httpRequest<SetAuthenticationMethodResponse>(url, requestInit)
      ).data;

      if (!setAuthenticationMethodData) throw new Error("Empty http response");

      setSnackbar((prev) => ({
        ...prev,
        message: setAuthenticationMethodData.message,
        open: true,
        severity: "success",
      }));

      return setAuthenticationMethodData;
    } catch (error) {
      const message =
        "Failed to set user's authentication method. Please try again later.";
      console.error(message, error);

      setSnackbar((prev) => ({
        ...prev,
        message: getErrorMessage(error),
        open: true,
        severity: "error",
      }));
    } finally {
      setLoading(false);
    }
  }

  async function updateAuthenticationMethod(request: AuthenticationRequest) {
    const method = "PATCH";
    return await setAuthenticationMethod({ ...request, method });
  }

  return (
    <MultifactorContext.Provider
      value={{
        addAuthenticationMethod,
        data,
        deleteAuthenticationMethod,
        getAuthenticationMethods,
        metadata,
        setData,
        setMetadata,
        updateAuthenticationMethod,
      }}
    >
      {children}
    </MultifactorContext.Provider>
  );
}

export function useMultifactor() {
  const ctx = useContext(MultifactorContext);
  if (!ctx)
    throw new Error("useMultifactor must be used within a MultifactorContext");
  return ctx;
}
